import { leggiChiave, writeKey, removeKey } from './storage';
import { applyTokenTenant, clearTokenTenant } from '@/shared/theme/applicaTokenTenant';
import { contrast, searchContrast, TEXT_THRESHOLD } from '@/shared/theme/generaToken';
import type { DetailSurfaceKind } from '@/shared/components/DetailSurface';
import { STORAGE_KEYS } from './constants';
import { applyStatusBarColor } from './nativeChrome';
/**
 * Tema/layout UI per-tenant (Fase 3).
 *
 * Forma del blob serializzato in `tenants.uiThemeJson` + helper per applicarlo
 * come CSS variable su <html> e per la cache anti-FOUC in localStorage.
 *
 * L'applicazione NON tocca direttamente i componenti: scrive solo CSS variable
 * (consumate da AppShell.tsx e da `app-shell.css`), così il default resta
 * identico quando il tenant non ha un tema impostato.
 */

export interface UiTheme {
  /** Colore di sfondo dell'header (hex, es. "#1c7ed6"). Se `headerGradient` è
   *  attivo, è il colore di partenza della sfumatura. */
  headerColor?: string | null;
  /** Colore di sfondo della sidebar (hex). Colore di partenza se `sidebarGradient`. */
  sidebarColor?: string | null;
  /** Secondo colore (arrivo) della sfumatura header. */
  headerColor2?: string | null;
  /** Secondo colore (arrivo) della sfumatura sidebar. */
  sidebarColor2?: string | null;
  /** Sfumatura header attiva: header passa da `headerColor` a `headerColor2`. */
  headerGradient?: boolean | null;
  /** Sfumatura sidebar attiva. */
  sidebarGradient?: boolean | null;
  /** Angolo della sfumatura header in gradi CSS (0=verso l'alto, 90=verso destra, 180=verso il basso). Default 90. */
  headerGradientAngle?: number | null;
  /** Angolo della sfumatura sidebar in gradi CSS. Default 180. */
  sidebarGradientAngle?: number | null;
  /** Tonalità del testo nell'header. Assente = automatico, lo decide il contrasto. */
  headerText?: InkTone;
  /** Tonalità del testo nella sidebar. Assente = automatico, lo decide il contrasto. */
  sidebarText?: InkTone;
  /** Ombra sotto l'header. */
  headerShadow?: boolean | null;
  /** Ombra a lato della sidebar. */
  sidebarShadow?: boolean | null;

  /*
   * COLORE FISSO O COLORE DEL TEMA — la scelta che comanda tutte le altre, una per pezzo.
   *
   * `true` = **fisso**: la tinta scelta dall'azienda vale in tema chiaro e in tema scuro, sempre
   * la stessa. `false` = **segue il tema**: la tinta non si applica affatto, e il colore lo
   * deriva il generatore dalla marca — la barra resta di marca nei due modi, la colonna è la
   * barra in chiaro e la superficie della pagina in scuro. Nell'editor, in questo secondo caso i
   * selettori di colore **spariscono**: non sono disattivati, non ci sono, perché non c'è niente
   * da scegliere.
   *
   * ⚠️ **Assente non è `false`: assente vuol dire «lo dice il colore».** `headerColorFixed`
   * assente con un `headerColor` scritto = fisso, perché quella tinta qualcuno l'ha scelta e fino
   * a ieri la vedeva. Assente senza colore = segue il tema, che è lo stato di chi non ha mai
   * toccato niente. È la regola che fa passare le righe già scritte senza chiedere niente a
   * nessuno, ed è il motivo per cui il campo è tri-stato invece che booleano.
   *
   * ⚠️ **Il colore resta scritto anche in «segue il tema»**, e non è una svista: passando da un
   * modo all'altro e tornando indietro, la tinta di prima è ancora lì. Cancellarla renderebbe
   * distruttiva una scelta che si prende per provare.
   *
   * ⚠️ **Cosa cambia rispetto a prima del 25-09-2026.** Fino a quel giorno la tinta valeva in
   * chiaro e in scuro **no**, senza che niente lo dicesse: chi la sceglieva vedeva la colonna
   * tornare scura e concludeva che l'impostazione fosse rotta. Era la correzione del 07-09 — una
   * colonna chiara alta tutto lo schermo accanto a una pagina nera — presa dal verso sbagliato:
   * risolveva da sé un caso che ora l'azienda decide, e lo risolveva in silenzio. Chi ha una
   * tinta pallida ora **la vede anche al buio**: è il prezzo dichiarato del modo «fisso», e per
   * non pagarlo c'è l'altro modo.
   *
   * ⚠️ **Nomi nostri dentro un blob condiviso.** `uiThemeJson` è la stessa colonna che scrive
   * Lares: due chiavi che solo noi conosciamo non rompono niente (chi non le conosce le ignora),
   * ma il giorno che di là si vuole la stessa cosa con un altro nome i due prodotti si ignorano
   * a vicenda senza nessun errore. Stanno in `docs/FE-A-LARES-CHIAVI-PREFERENZE-v1.md`.
   */
  /** La barra in alto ha un colore fisso. Assente = lo dice `headerColor`. */
  headerColorFixed?: boolean | null;
  /** La colonna del menù ha un colore fisso. Assente = lo dice `sidebarColor`. */
  sidebarColorFixed?: boolean | null;

  /*
   * LA BARRA DI NAVIGAZIONE MOBILE — forma e comportamento, scelti dall'azienda.
   *
   * ⚠️ **I nomi dei campi sono un contratto con Lares, non una scelta di questo repo.** Lares
   * scrive in **questo stesso** `uiThemeJson`, dello stesso tenant: i due prodotti leggono la
   * stessa riga dello stesso database. Nomi diversi, o valori diversi, significano che
   * un'azienda configurata da Lares non viene rispettata da qui e viceversa — senza nessun
   * errore, sulla stessa colonna.
   *
   * ⚠️ **Oggi i due nomi divergono, e la conseguenza e' viva.** In Lares sono `barraForma` e
   * `barraRitiro`; qui sono passati all'inglese, quindi un `uiThemeJson` scritto da Lares porta
   * chiavi che da qui non si leggono e la cascata scende al predefinito invece di obbedire
   * all'azienda. Non e' una decisione presa qui la prima volta: il lotto della traduzione del
   * 18-09-2026 li ha rinominati insieme al resto, non riconoscendoli come nomi-dato. Il 19-09 si
   * e' scelto di **completare** l'allineamento invece di tornare indietro — corrispondenze e
   * righe da migrare in `docs/FE-A-LARES-CHIAVI-PREFERENZE-v1.md`.
   *
   * ⚠️ **I valori restano italiani** (`'staccata'`, `'filo'`): sono dato scritto nella stessa
   * colonna, e tradurli e' una seconda decisione, non il seguito di questa.
   *
   * ⚠️ **`null` non e' `false`**, ed e' la distinzione che fa funzionare la cascata (persona →
   * azienda → predefinito): su `null` si scende al gradino successivo, su `false` si obbedisce.
   * Appiattendo i due casi, un'azienda che non ha mai deciso niente impedirebbe comunque alla
   * scelta della persona di valere. Per questo l'editor deve poter esprimere «non decidere», e
   * non due soli stati.
   */
  /** Forma della barra inferiore. `null` = non decide, sceglie la persona o il predefinito. */
  barShape?: 'staccata' | 'filo' | null;
  /** La barra si ritira scorrendo. `null` = non decide. */
  barAutoHide?: boolean | null;

  /**
   * IL PIEDE DELLA PAGINA: fermo in basso, che scorre col contenuto, oppure niente.
   *
   * `null`/assente = **non decide**, e si scende al gradino successivo: la riga `Setup`
   * dell'installazione, che è ciò che l'amministratore di piattaforma imposta da «Sistema». È la
   * stessa cascata di `barShape` e per la stessa ragione — un'azienda che non ha mai scelto non
   * deve impedire la scelta di chi sta sopra.
   *
   * ⚠️ **Chiave nostra dentro un blob condiviso.** Lares ha `Setup.fixedFooter`, un booleano
   * dell'**installazione**: non ha un piede per azienda, quindi questo nome lo scriviamo solo
   * noi. Sta in `docs/FE-A-LARES-CHIAVI-PREFERENZE-v1.md` insieme agli altri, perché il giorno
   * che di là serve la stessa cosa i due prodotti devono chiamarla uguale.
   *
   * ⚠️ Vale per la **cornice da scrivania**: sul telefono il piede non c'è, al suo posto c'è la
   * barra in basso, che si configura due card più su.
   */
  footerMode?: FooterMode | null;

  /**
   * Con quale superficie si apre il dettaglio di una scheda.
   *
   * `null` = non decide, sceglie la persona o il predefinito — la stessa regola
   * di `barShape` qui sopra, e per lo stesso motivo.
   *
   * ⚠️ Il nome e' **nostro**, a differenza dei due campi della barra: se un giorno
   * Lares scrive qui la stessa cosa con un altro nome, i due prodotti si ignorano
   * a vicenda sulla stessa colonna, senza nessun errore.
   */
  detailSurface?: DetailSurfaceKind | null;
}

/**
 * Il modo di un pezzo della cornice, con la regola dell'assenza applicata in un posto solo.
 *
 * ⚠️ Esportata perché la leggono in due — chi disegna la cornice e chi la configura — e una
 * regola dell'assenza scritta due volte è una regola che fra due mesi dice due cose: l'editor
 * aprirebbe su «segue il tema» una cornice che a schermo è fissa, cioè mostrerebbe una scelta
 * che nessuno ha fatto e che salvandola cambierebbe l'aspetto senza volerlo.
 */
export function frameColorIsFixed(declared: boolean | null | undefined, color: string | null | undefined): boolean {
  return declared ?? Boolean(color);
}

export const UI_THEME_CACHE_KEY = STORAGE_KEYS.uiThemeCache;

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Il tono dell'inchiostro. `null`/assente = **automatico**: lo decide il contrasto. */
export type InkTone = 'light' | 'dark' | null;

/**
 * Dove sta il piede della pagina, e se c'è.
 *
 * ⚠️ Il tipo vive **qui** e non accanto a chi lo legge, perché il valore ormai arriva da due
 * dati diversi: la riga `Setup` dell'installazione (`fixedFooter`) e questo blob per azienda.
 * `features/admin/setupApp.ts` lo ri-esporta per chi lo importava da lì — una union scritta due
 * volte è una union che fra sei mesi ha tre valori da una parte e quattro dall'altra.
 */
export type FooterMode = 'fixed' | 'scrolling' | 'hidden';

const INK_LIGHT = 'rgba(255, 255, 255, 0.95)';
const INK_DARK = '#0F1115';
/** La soglia del testo, la stessa del generatore e di Lares. */
const INK_THRESHOLD = 4.5;

/**
 * L'INCHIOSTRO CHE SI LEGGE SOPRA UN FONDO SCELTO DALL'AZIENDA.
 *
 * ## Le tre risposte, e cosa vuol dire «automatico»
 *
 * · **assente** (`null`) — lo decide il contrasto: fra bianco e quasi-nero vince il più
 *   contrastato sul fondo scelto. È il predefinito, ed è quello che va bene quasi sempre;
 * · **`'light'` / `'dark'`** — l'azienda impone il tono, **e vince se regge**: se sopra quel fondo
 *   non arriva a 4,5:1, si usa comunque quello leggibile. Una preferenza di gusto non può
 *   produrre una testata che non si legge, e chi l'ha scelta lo scopre dalla console invece che
 *   da un utente.
 *
 * ⚠️ **Fino al 25-09-2026 i toni erano due e uno dei due mentiva.** `'light'` forzava il bianco
 * *sempre*, anche su un fondo chiarissimo; `'dark'` non scriveva niente e cadeva nel calcolo
 * automatico — cioè l'etichetta «Scuro» faceva una cosa che non era né scura né dichiarata, e
 * **imporre il testo scuro non era possibile**. In mezzo mancava proprio la voce che descriveva
 * il comportamento reale, «Automatico», che è anche il valore che Lares scrive (campo assente).
 *
 * ⚠️ **La migrazione è a costo zero, ed è il motivo per cui la soglia viene prima della
 * rietichettatura.** Ogni riga scritta da qui porta `'dark'`, che significava «automatico»: con
 * la soglia, `'dark'` su un fondo chiaro dà quasi-nero (come prima) e su un fondo scuro non regge
 * e ricade sul bianco (come prima). Stesso pixel, in tutti e due i casi. Quello che cambia è
 * `'light'` su un fondo chiaro, che oggi è illeggibile e da domani si corregge.
 *
 * ⚠️ **Esportata**: la usa anche l'anteprima del branding. Il rischio che chiude è quello che
 * un'anteprima ha per mestiere — dire una cosa e farne un'altra — e qui sarebbe invisibile,
 * perché sbaglierebbe solo sui fondi al limite del contrasto, cioè proprio quelli che si va a
 * guardare nell'anteprima prima di salvare.
 *
 * ## ⚠️ Sulla sfumatura si misura il capo peggiore, non il primo
 *
 * Un fondo che va da bordeaux a rosa chiaro non regge **nessun** inchiostro: quello che si legge a
 * sinistra sparisce a destra. Misurare il colore di partenza — come si faceva — dà una risposta
 * giusta su metà superficie, che è il modo più lungo per dire sbagliata. Lares, oltre a
 * misurarli entrambi, **stringe la sfumatura** finché regge; qui per ora la si misura soltanto, e
 * quando non regge da nessuna parte si sceglie il meno peggio e lo si dice.
 */
export function resolveInkTone(
  backgrounds: readonly (string | null | undefined)[],
  tone: InkTone,
): 'light' | 'dark' | null {
  const solid = backgrounds
    .map((b) => b?.trim() ?? '')
    .filter((b) => /^#[0-9a-fA-F]{3,8}$/.test(b));
  /* Nessun fondo leggibile = nessun fondo scritto (`bgValue` torna `null`), quindi nemmeno
     l'inchiostro: scriverlo da solo è il difetto al rovescio — un testo scelto per un fondo che
     non c'è. */
  if (solid.length === 0) return null;

  const worst = (ink: string) => Math.min(...solid.map((b) => contrast(ink, b)));
  const lightRatio = worst('#FFFFFF');
  const darkRatio = worst(INK_DARK);
  const best: 'light' | 'dark' = lightRatio >= darkRatio ? 'light' : 'dark';

  if (!tone) return best;
  const asked = tone === 'light' ? lightRatio : darkRatio;
  if (asked >= INK_THRESHOLD) return tone;
  /* ⚠️ Si scavalca la scelta dell'azienda, quindi **lo si dice**: chi ha configurato quel tema
     deve poter capire perché vede un colore che non ha chiesto, senza aprire questo file. */
  console.warn(
    `[branding] il testo «${tone === 'light' ? 'chiaro' : 'scuro'}» non si legge sul fondo scelto `
    + `(${asked.toFixed(2)}:1, serve ${INK_THRESHOLD}): uso l'altro.`,
  );
  return best;
}

/** Il valore CSS dell'inchiostro risolto, o `null` se non c'è un fondo su cui misurarlo. */
function inkAbove(backgrounds: readonly (string | null | undefined)[], tone: InkTone): string | null {
  const resolved = resolveInkTone(backgrounds, tone);
  if (!resolved) return null;
  return resolved === 'light' ? INK_LIGHT : INK_DARK;
}

/**
 * Valore CSS per lo sfondo di header/sidebar. Ritorna:
 * - `null` se il colore base manca/non valido → nessun override (resta il default);
 * - un `linear-gradient(...)` se la sfumatura è attiva e il secondo colore è valido;
 * - il colore pieno altrimenti.
 * Le variabili sono consumate con `background:` (non `background-color:`), quindi
 * reggono sia il colore pieno sia il gradiente.
 */
function bgValue(
  c1?: string | null,
  c2?: string | null,
  gradient?: boolean | null,
  angle?: number | null,
  defaultAngle = 90,
): string | null {
  if (!c1 || !HEX_RE.test(c1)) return null;
  if (gradient && c2 && HEX_RE.test(c2)) {
    const a = typeof angle === 'number' && Number.isFinite(angle) ? angle : defaultAngle;
    return `linear-gradient(${a}deg, ${c1}, ${c2})`;
  }
  return c1;
}

/** Ombre coerenti col tema Mantine del progetto (vedi AppProviders → shadows). */
const HEADER_SHADOW = '0 2px 6px -2px rgba(15, 23, 42, 0.10), 0 4px 12px -4px rgba(15, 23, 42, 0.12)';
const SIDEBAR_SHADOW = '4px 0 16px -6px rgba(15, 23, 42, 0.14)';

const VARS = [
  '--app-header-bg', '--app-header-fg', '--app-header-shadow',
  '--app-sidebar-bg', '--app-sidebar-fg', '--app-sidebar-shadow', '--app-sidebar-active',
] as const;

const ATTRS = ['data-app-header-themed-fg', 'data-app-sidebar-themed-fg', 'data-app-sidebar-fixed'] as const;

const SCHEMA_ATTRIBUTE = 'data-mantine-color-scheme';

/** Il tema scelto dall'azienda, tenuto da parte per poterlo riapplicare al cambio di schema. */
let lastTheme: UiTheme | null = null;
let schemaObserver: MutationObserver | null = null;

/**
 * LA VOCE ATTIVA DEL MENÙ SI RICALCOLA SOPRA IL FONDO SCELTO, e non è una rifinitura.
 *
 * `--sidebar-attivo` lo emette il generatore come `cercaContrasto(marca, fondoColonna, 4,5)`, dove
 * `fondoColonna` è quello **del tema**, cioè la superficie della pagina nei due modi.
 * Nel momento in cui l'azienda mette un fondo suo, quel calcolo è stato fatto contro un colore
 * che non c'è più — e il caso peggiore è la colonna chiara tenuta in tema scuro, dove la voce
 * attiva è un rosso schiarito apposta per stare sul nero e finisce su un azzurro pallidissimo.
 *
 * ⚠️ **Non è un difetto del tema scuro.** Lo stesso succedeva già in chiaro a chiunque scegliesse
 * una colonna diversa dalla propria barra, che è esattamente ciò che questa impostazione serve a
 * fare. Il ricalcolo si applica **ogni volta che il fondo lo scrive l'azienda**, nei due modi.
 *
 * ⚠️ ⚠️ **Si scrive `--app-sidebar-active`, non `--sidebar-attivo`, e la differenza è tutta.**
 * I token del generatore stanno nello `style` **in linea** di `<html>` (vedi
 * `applicaTokenTenant`), quindi sovrascriverne uno lo **distrugge**: non lo copre, lo sostituisce,
 * e il valore di prima non torna più — nemmeno togliendo il nostro, perché sotto c'è solo il
 * valore statico del foglio. La prima stesura faceva proprio questo, e il reset in testa a
 * `writeTheme` cancellava a ogni passata la voce attiva che il generatore aveva appena derivato
 * per l'azienda: un colore perso a ogni cambio di tema, senza che niente lo dicesse.
 *
 * Con una variabile propria e il ripiego nel componente
 * (`var(--app-sidebar-active, var(--sidebar-attivo))`) i due strati **convivono**, che è lo
 * stesso schema già in piedi per fondo e inchiostro: il tenant copre, e togliendo la copertura
 * riemerge intatto quel che c'era sotto.
 *
 * La marca si **legge dal documento** invece di ri-dedurla: è la stessa scelta di `topBarColor`
 * qui sotto, e per la stessa ragione — il giorno che la marca cambia strada, la voce attiva la
 * segue senza che nessuno se ne ricordi.
 *
 * Su una sfumatura si misura contro il colore di partenza, come fa `inkAbove` per l'inchiostro:
 * i due devono rispondere allo stesso fondo, o si contraddicono a metà colonna.
 */
function activeItemAbove(background: string | null): void {
  if (!background || !HEX_RE.test(background)) return;
  const brand = getComputedStyle(document.documentElement).getPropertyValue('--brand').trim();
  if (!/^#[0-9a-fA-F]{3,8}$/.test(brand)) return;
  document.documentElement.style.setProperty(
    '--app-sidebar-active',
    searchContrast(brand, background, TEXT_THRESHOLD),
  );
}

function writeTheme(): void {
  const root = document.documentElement;
  // Reset: parto sempre da pulito, poi re-imposto solo i valori valorizzati.
  for (const v of VARS) root.style.removeProperty(v);
  for (const a of ATTRS) root.removeAttribute(a);

  const theme = lastTheme;
  if (!theme) return;

  /* Il tema non c'entra più: decide il modo scelto per quel pezzo. Vedi `UiTheme`. */
  const headerApplies = frameColorIsFixed(theme.headerColorFixed, theme.headerColor);
  const sidebarApplies = frameColorIsFixed(theme.sidebarColorFixed, theme.sidebarColor);

  if (headerApplies) {
    const headerBg = bgValue(theme.headerColor, theme.headerColor2, theme.headerGradient, theme.headerGradientAngle, 90);
    if (headerBg) root.style.setProperty('--app-header-bg', headerBg);

    /* `headerBg` è il fondo appena calcolato: l'inchiostro si appaia a quello, non al fondo
       predefinito che quel fondo ha appena sostituito. */
    const hf = inkAbove([theme.headerColor, theme.headerGradient ? theme.headerColor2 : null], theme.headerText ?? null);
    if (hf) {
      root.style.setProperty('--app-header-fg', hf);
      root.setAttribute('data-app-header-themed-fg', 'true');
    }
  }

  if (sidebarApplies) {
    const sidebarBg = bgValue(theme.sidebarColor, theme.sidebarColor2, theme.sidebarGradient, theme.sidebarGradientAngle, 180);
    if (sidebarBg) root.style.setProperty('--app-sidebar-bg', sidebarBg);

    const sf = inkAbove([theme.sidebarColor, theme.sidebarGradient ? theme.sidebarColor2 : null], theme.sidebarText ?? null);
    if (sf) {
      root.style.setProperty('--app-sidebar-fg', sf);
      root.setAttribute('data-app-sidebar-themed-fg', 'true');
    }

    if (sidebarBg) activeItemAbove(theme.sidebarColor ?? null);
  }

  if (theme.headerShadow) root.style.setProperty('--app-header-shadow', HEADER_SHADOW);
  if (theme.sidebarShadow) root.style.setProperty('--app-sidebar-shadow', SIDEBAR_SHADOW);
}

/**
 * Il colore che la barra in cima **mostra davvero**, letto dal documento invece che ri-dedotto.
 *
 * ⚠️ Rispecchia la catena di `.mw-appbar` (`theme.css`): `--app-header-bg`, poi `--brand-fill`, e
 * in ultimo `--brand`. Non e' una scorciatoia — e' il punto: dedurlo di nuovo da `UiTheme` vorrebbe dire
 * riscrivere qui la regola del chiaro/scuro, quella della sfumatura e quella del ripiego, cioe'
 * tre occasioni di scollarsi dalla barra vera. Letto dal documento, il giorno che la barra cambia
 * colore la striscia lo segue **senza che nessuno se ne ricordi**.
 *
 * Su una sfumatura si prende il **primo** colore: e' quello del bordo sinistro, cioe' quello che
 * tocca la striscia dalla parte da cui si comincia a leggere. Meglio del primo dei due che
 * capita, e molto meglio di lasciare la striscia com'era.
 */
function topBarColor(): string | null {
  if (typeof window === 'undefined') return null;
  const s = getComputedStyle(document.documentElement);
  /* ⚠️ `--brand-fill` **prima** di `--brand`: la barra e' una superficie piena, non un'identita'.
     Era `--brand` e basta, e in tema scuro dava la striscia di un colore e la barra di un altro
     — la cucitura in cima che questa funzione esiste per togliere. */
  const raw = s.getPropertyValue('--app-header-bg').trim()
    || s.getPropertyValue('--brand-fill').trim()
    || s.getPropertyValue('--brand').trim();
  return /#[0-9a-fA-F]{3,8}/.exec(raw)?.[0] ?? null;
}

/**
 * Applica (o pulisce) il tema come CSS variable + data-attribute su <html>.
 *
 * ⚠️ L'osservatore serve perche' il tema si cambia **a caldo**, dal menu utente: senza, la
 * correzione qui sopra funzionerebbe solo per chi apre l'app gia' in scuro, e chi scambia
 * si ritroverebbe la colonna bianca fino al prossimo caricamento. E' la stessa ragione per cui
 * ce n'e' uno in `applicaTokenTenant`.
 */
export function applyUiTheme(theme: UiTheme | null | undefined): void {
  lastTheme = theme ?? null;
  writeTheme();
  brushStrip();

  if (!lastTheme) {
    schemaObserver?.disconnect();
    schemaObserver = null;
    return;
  }
  if (!schemaObserver) {
    schemaObserver = new MutationObserver(() => { writeTheme(); brushStrip(); });
    schemaObserver.observe(document.documentElement, { attributeFilter: [SCHEMA_ATTRIBUTE, 'data-theme'] });
  }
}

/**
 * LA STRISCIA DI STATO DEL TELEFONO PRENDE IL COLORE DELLA BARRA, e sta qui perche' qui si decide
 * quel colore.
 *
 * ## Il difetto
 *
 * La chiamata stava in `brandingRisolto` e le passava `primaryColor`, cioe' la **marca**. Ma la
 * barra prende `--app-header-bg`, cioe' `uiTheme.headerColor`: sono due campi diversi, scelti in
 * due punti diversi di `admin/branding`. Un'azienda che sceglie una testata diversa dalla propria
 * marca — ed e' il caso di chi la vuole pallida — si vedeva in cima **due tinte impilate**, la
 * striscia della marca e la barra della testata, con la cucitura in mezzo.
 *
 * ## Perche' qui e non nel componente
 *
 * ⚠️ `StatusBarOverlaysWebView = false` (`APP/config.xml`): la WebView comincia **sotto** la
 * striscia, quindi quel rettangolo non e' disegnabile dal CSS — nessun componente puo'
 * colorarlo, si puo' solo chiedere al plugin. E il colore da chiedere e' quello che la barra
 * mostra **adesso**, che dipende anche dal chiaro/scuro: in scuro le tinte della cornice non si
 * scrivono e vale il ripiego. Attaccata a `scriviTema` — e all'osservatore che lo rilancia — la
 * striscia segue il tema **a caldo**, che e' il modo in cui il tema si cambia davvero. Nel React
 * sarebbe rimasta indietro di un cambio.
 *
 * Sul web e in `jsdom` non fa niente: `applicaColoreStatusBar` esce subito fuori da un guscio
 * nativo.
 */
function brushStrip(): void {
  applyStatusBarColor(topBarColor());
}

/** Parse difensivo del blob JSON da `tenants.uiThemeJson`. */
export function parseUiTheme(json?: string | null): UiTheme | null {
  if (!json) return null;
  try {
    const obj = JSON.parse(json) as unknown;
    return obj && typeof obj === 'object' ? (obj as UiTheme) : null;
  } catch {
    return null;
  }
}

export function readCachedUiTheme(): UiTheme | null {
  try {
    return parseUiTheme(leggiChiave(UI_THEME_CACHE_KEY));
  } catch {
    return null;
  }
}

export function writeCachedUiTheme(theme: UiTheme | null): void {
  try {
    if (theme) writeKey(UI_THEME_CACHE_KEY, JSON.stringify(theme));
    else removeKey(UI_THEME_CACHE_KEY);
  } catch {
    /* storage non disponibile: ignoro */
  }
}

/* ───────────────────────── Colori brand del tenant ───────────────────────── */

const BRAND_VARS = [
  '--mantine-color-brand-filled',
  '--mantine-color-brand-6',
  '--mantine-primary-color-filled',
  '--mantine-primary-color-6',
  '--mantine-color-secondary',
] as const;

/**
 * Sovrascrive il colore primario (e il secondario) del tema con quelli del tenant.
 *
 * Passa per le CSS variable di Mantine invece che per un `MantineProvider` annidato: il
 * tema è creato una volta sola all'avvio e i colori del tenant arrivano dopo, da una query.
 * Ricreare il tema a ogni cambio rimonterebbe l'albero sotto il provider.
 *
 * Un colore non valido **non** viene scritto: meglio il bordeaux di default che una
 * variabile CSS rotta, che si porterebbe dietro ogni pulsante della pagina. Passare `null`
 * a entrambi ripulisce — serve uscendo dal portale, dove il tenant che fa da host cambia.
 */
export function applyBrandColors(primary?: string | null, secondary?: string | null): void {
  const root = document.documentElement;
  if (primary && HEX_RE.test(primary)) {
    root.style.setProperty('--mantine-color-brand-filled', primary);
    root.style.setProperty('--mantine-color-brand-6', primary);
    root.style.setProperty('--mantine-primary-color-filled', primary);
    root.style.setProperty('--mantine-primary-color-6', primary);
  }
  if (secondary && HEX_RE.test(secondary)) {
    root.style.setProperty('--mantine-color-secondary', secondary);
  }

  /*
   * ⚠️ Le righe qui sopra erano **tutto** quello che il colore di un'azienda toccava, e sono le
   * primitive di Mantine. Le migliaia di letture passate da `mw.*` e da `var(--*)` restavano di
   * marca: chi sceglieva il verde otteneva bottoni verdi e bordeaux dappertutto — avatar,
   * riquadri-icona, hero, accenti dei KPI. Da qui la tavolozza intera si **deriva** da marca +
   * accento, con la ricerca del contrasto dentro.
   */
  if (primary && HEX_RE.test(primary)) {
    applyTokenTenant(primary, secondary && HEX_RE.test(secondary) ? secondary : null);
  }
}

/** Rimuove gli override di colore: si torna all'identità MaeDesign di default. */
export function clearBrandColors(): void {
  const root = document.documentElement;
  for (const v of BRAND_VARS) root.style.removeProperty(v);
  clearTokenTenant();
}
